import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const ProtectedRoute = ({ children, requiredRole }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, loading, verifyAuth } = useAuth();
  const [isValidating, setIsValidating] = useState(true);

  useEffect(() => {
    const validateAccess = async () => {
      // If still loading from context, wait
      if (loading) {
        return;
      }

      // If user was just set by AuthCallback via navigation state, trust it
      if (location.state?.user && isAuthenticated === true && user) {
        setIsValidating(false);
        return;
      }

      // If not authenticated yet (null = initial), verify with server
      if (isAuthenticated === null) {
        const verifiedUser = await verifyAuth();
        if (!verifiedUser) {
          navigate('/login', { replace: true });
          return;
        }
      }

      // Check if user is authenticated
      if (isAuthenticated === false) {
        navigate('/login', { replace: true });
        return;
      }

      // If authenticated but no user data yet, wait
      if (!user) {
        return;
      }

      // Check if user needs to select role
      if (!user.role) {
        navigate('/role-select', { state: { user }, replace: true });
        return;
      }

      // Check if user has required role
      if (requiredRole && user.role !== requiredRole) {
        if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (user.role === 'teacher') {
          navigate('/teacher/dashboard', { replace: true });
        } else if (user.role === 'student') {
          navigate('/student/dashboard', { replace: true });
        } else {
          navigate('/login', { replace: true });
        }
        return;
      }

      // All checks passed
      setIsValidating(false);
    };

    validateAccess();
  }, [user, isAuthenticated, loading, requiredRole, navigate, verifyAuth, location.state]);

  // Show loading while validating
  if (loading || isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
          <p className="mt-4 text-slate-600 font-nunito">Carregando...</p>
        </div>
      </div>
    );
  }

  // Render children if authenticated and authorized
  if (isAuthenticated && user && (!requiredRole || user.role === requiredRole)) {
    return children;
  }

  // Fallback - show loading instead of blank screen while redirect happens
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8FAFC]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-primary mx-auto"></div>
        <p className="mt-4 text-slate-600 font-nunito">Carregando...</p>
      </div>
    </div>
  );
};

export default ProtectedRoute;
